import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiMessageCircle, FiPhone, FiUserPlus, FiUsers } from "react-icons/fi";
import salesService from "../../api/services/salesService";
import SalesPerformancePanel from "./SalesPerformancePanel";
import SalesCustomerChat from "./SalesCustomerChat";
import { TableSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const BLOCK_REASONS = ["Spam", "Abusive", "No response", "Duplicate", "Other"];

const ALLOWED = ["Admin", "Developer", "Sales", "Sales Team Lead"];
const MAX_BATCH = 100;

function getCurrentAdminId() {
  try {
    const raw = localStorage.getItem("adminUserDetails");
    if (raw) return JSON.parse(raw)?.id ?? null;
  } catch {
    /* ignore */
  }
  return null;
}

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SalesCrmPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isSalesAgent = userRole === "Sales" || userRole === "Sales Team Lead";
  const isSalesLead = userRole === "Sales Team Lead";
  const isCsr = userRole === "CSR" || userRole === "CSR Team Lead";

  const [tab, setTab] = useState(isAdmin ? "performance" : isCsr ? "assign" : "mine");
  const [stages, setStages] = useState([]);
  const [pool, setPool] = useState([]);
  const [mine, setMine] = useState([]);
  const [team, setTeam] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [assignSelf, setAssignSelf] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [quota, setQuota] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [blockModal, setBlockModal] = useState(null);
  const [callModal, setCallModal] = useState(null);
  const [chatCustomer, setChatCustomer] = useState(null);
  const [blockReason, setBlockReason] = useState(BLOCK_REASONS[0]);
  const [blockDetail, setBlockDetail] = useState("");

  const currentAdminId = getCurrentAdminId();

  const loadQuota = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      const data = await salesService.getAssignQuota(Number(agentId));
      setQuota(data);
    } catch {
      setQuota(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (isCsr) {
        if (tab === "assign") {
          const [poolData, agentsData] = await Promise.all([
            salesService.getCustomerPool({ search: debouncedSearch }),
            salesService.getSalesAgents(),
          ]);
          setPool(poolData.customers || []);
          setAssignSelf(agentsData.self || null);
          setSalesAgents(agentsData.agents || []);
          const defaultId = String(agentsData.defaultAgentId || agentsData.self?.id || currentAdminId || "");
          setSelectedAgentId((prev) => (prev && defaultId ? prev : defaultId));
          if (defaultId) await loadQuota(defaultId);
        } else if (tab === "assignments") {
          const [data, stagesRes] = await Promise.all([
            salesService.getTeamCustomers({ search: debouncedSearch }),
            salesService.getPipelineStages(),
          ]);
          setTeam(data.customers || []);
          setStages(stagesRes.stages || []);
        }
        return;
      }

      const [stagesRes, statsRes] = await Promise.all([
        salesService.getPipelineStages(),
        salesService.getStats(),
      ]);
      setStages(stagesRes.stages || []);
      setStats(statsRes.stats || null);

      if (tab === "pool") {
        const data = await salesService.getCustomerPool({ search: debouncedSearch });
        setPool(data.customers || []);
        if (currentAdminId) await loadQuota(currentAdminId);
      } else if (tab === "team" && isSalesLead) {
        const data = await salesService.getTeamCustomers({ search: debouncedSearch });
        setTeam(data.customers || []);
      } else if (tab === "mine") {
        const data = await salesService.getMyCustomers({ search: debouncedSearch });
        setMine(data.customers || []);
      }
    } catch {
      toast.error("Failed to load sales CRM data");
    } finally {
      setLoading(false);
    }
  }, [tab, debouncedSearch, isAdmin, isSalesLead, isCsr, currentAdminId, loadQuota]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const agentId = isCsr
      ? Number(selectedAgentId) || currentAdminId
      : tab === "pool"
        ? currentAdminId
        : null;
    if (agentId) loadQuota(agentId);
  }, [selectedAgentId, isCsr, tab, loadQuota, currentAdminId]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab, debouncedSearch, selectedAgentId]);

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  const effectiveAgentId = isCsr
    ? Number(selectedAgentId) || currentAdminId
    : currentAdminId;

  const remainingSlots = quota?.remainingSlots ?? MAX_BATCH;
  const canSelectMore = (id) => selectedIds.has(id) || selectedIds.size < remainingSlots;

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else if (next.size < remainingSlots) next.add(userId);
      else toast.error(`You can only select up to ${remainingSlots} customer(s) in this batch`);
      return next;
    });
  };

  const selectAllVisible = () => {
    const ids = list.map((c) => c.userId).filter(Boolean);
    const take = ids.slice(0, remainingSlots);
    setSelectedIds(new Set(take));
    if (ids.length > remainingSlots) {
      toast(`Selected ${remainingSlots} (batch limit). Resolve current batch to assign more.`);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runAssign = async (userIds) => {
    const ids = [...new Set(userIds.map(Number).filter(Boolean))];
    if (!ids.length) return;
    const agentId = isCsr ? effectiveAgentId : currentAdminId;
    if (!agentId) {
      toast.error("Could not determine assignee. Please re-login.");
      return;
    }
    setAssigning(true);
    try {
      if (ids.length === 1 && !isCsr) {
        await salesService.selfAssign(ids[0]);
        toast.success("Customer assigned to you");
      } else if (ids.length === 1 && isCsr) {
        await salesService.assignToAgent(ids[0], agentId);
        toast.success(agentId === currentAdminId ? "Customer assigned to you" : "Customer assigned");
      } else {
        const res = await salesService.assignBulk(ids, agentId);
        const n = res.results?.assigned?.length ?? ids.length;
        const failed = res.results?.failed?.length ?? 0;
        if (failed) toast.error(`${failed} assignment(s) failed`);
        toast.success(`Assigned ${n} customer(s)`);
        if (res.quota) setQuota(res.quota);
      }
      clearSelection();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to assign");
      if (e?.response?.data?.quota) setQuota(e.response.data.quota);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssign = async (userId) => runAssign([userId]);

  const handleBulkAssign = () => runAssign([...selectedIds]);

  const handleStageChange = async (assignmentId, stageId) => {
    try {
      await salesService.updateStage(assignmentId, stageId);
      toast.success("Pipeline stage updated");
      refresh();
    } catch {
      toast.error("Failed to update stage");
    }
  };

  const handleBlock = async () => {
    if (!blockModal) return;
    try {
      await salesService.blockCustomer(blockModal.userId, {
        reason: blockReason,
        reason_detail: blockDetail,
      });
      toast.success("Customer blocked");
      setBlockModal(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to block customer");
    }
  };

  const handleLogCall = async () => {
    if (!callModal) return;
    try {
      const duration = Number(callModal.duration_sec) || 0;
      const started = new Date();
      started.setSeconds(started.getSeconds() - duration);
      await salesService.logCall({
        user_id: callModal.userId,
        channel: "sales",
        duration_sec: duration,
        started_at: started.toISOString().slice(0, 19).replace("T", " "),
        notes: callModal.notes || "",
      });
      toast.success("Call logged");
      setCallModal(null);
      refresh();
    } catch {
      toast.error("Failed to log call");
    }
  };

  const handleContact = (c) => {
    setChatCustomer({
      userId: c.userId,
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email,
    });
  };

  const list = isCsr
    ? tab === "assign"
      ? pool
      : team
    : tab === "pool"
      ? pool
      : tab === "team"
        ? team
        : mine;

  const showPoolActions = tab === "pool" || (isCsr && tab === "assign");
  const showAssignedActions = !showPoolActions && isSalesAgent;
  const showCsrAssignmentActions = isCsr && tab === "assignments";

  if (isAdmin) {
    return (
      <div className="p-4 w-full">
        <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers style={{ color: THEME.primary }} /> Sales CRM
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              View sales team performance, revenue, and payment records
            </p>
          </div>
          <SalesPerformancePanel />
        </div>
      </div>
    );
  }

  if (isCsr) {
    return (
      <div className="p-4 w-full">
        <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers style={{ color: THEME.primary }} /> Sales Customer Assign
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Assign customers to yourself or a sales agent · max {MAX_BATCH} active per batch
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "assign", label: "Assign Customer" },
              { id: "assignments", label: "All Assignments" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  tab === t.id ? "text-white shadow" : "bg-gray-100 text-gray-600"
                }`}
                style={tab === t.id ? { backgroundColor: THEME.primary } : {}}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "assign" && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Assign to</label>
                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className={`${inputCls} w-full max-w-md`}
                >
                  {assignSelf && (
                    <option value={assignSelf.id}>
                      Me — assign to myself ({assignSelf.unresolved ?? 0}/{MAX_BATCH} active,{" "}
                      {assignSelf.remainingSlots ?? MAX_BATCH} slots left)
                    </option>
                  )}
                  {salesAgents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.firstName} {a.lastName} — Sales ({a.unresolved ?? 0}/{MAX_BATCH} active,{" "}
                      {a.remainingSlots ?? 0} slots left)
                    </option>
                  ))}
                </select>
              </div>
              {quota && (
                <div
                  className={`rounded-lg px-4 py-2 text-sm ${
                    quota.canAssignMore ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
                  }`}
                >
                  <strong>Batch limit:</strong> {quota.activeUnresolved}/{quota.maxBatch} active customers ·{" "}
                  <strong>{quota.remainingSlots}</strong> slot(s) available
                  {quota.resolvedToday > 0 && (
                    <span> · {quota.resolvedToday} resolved today — assign next batch when active hits 0</span>
                  )}
                  {!quota.canAssignMore && (
                    <span className="block mt-1">
                      Resolve current customers (mark Converted or Lost) to unlock the next {MAX_BATCH}.
                    </span>
                  )}
                </div>
              )}
              {showPoolActions && quota?.canAssignMore !== false && (
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    onClick={selectAllVisible}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
                  >
                    Select all (up to {remainingSlots})
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
                  >
                    Clear selection
                  </button>
                  {selectedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={handleBulkAssign}
                      disabled={assigning}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                      style={{ backgroundColor: THEME.primary }}
                    >
                      Assign selected ({selectedIds.size})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputCls} w-full max-w-md mb-4`}
          />

          {loading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {showPoolActions && (
                      <th className="px-3 py-2 w-10">
                        <span className="sr-only">Select</span>
                      </th>
                    )}
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Premium</th>
                    {showCsrAssignmentActions && (
                      <>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Stage</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-500">Sales agent</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
                      </>
                    )}
                    {showPoolActions && (
                      <th className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.userId || c.assignmentId} className="border-t border-gray-100">
                      {showPoolActions && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.userId)}
                            disabled={!canSelectMore(c.userId)}
                            onChange={() => toggleSelect(c.userId)}
                            className="rounded border-gray-300"
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900">
                          {c.firstName} {c.lastName}
                        </p>
                        <p className="text-xs text-gray-400">{c.email}</p>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                            c.isPremium ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {c.isPremium ? "Premium" : "Free"}
                        </span>
                      </td>
                      {showCsrAssignmentActions && (
                        <>
                          <td className="px-3 py-2">
                            <select
                              value={stages.find((s) => s.stage_key === c.stageKey)?.id || ""}
                              onChange={(e) => handleStageChange(c.assignmentId, Number(e.target.value))}
                              className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                            >
                              {stages.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">{c.agentName || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex flex-wrap justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => handleContact(c)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                              >
                                <FiMessageCircle /> Contact
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setCallModal({ userId: c.userId, duration_sec: 5, notes: "" })
                                }
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700"
                              >
                                <FiPhone /> Log call
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                      {showPoolActions && (
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleAssign(c.userId)}
                            disabled={assigning || quota?.canAssignMore === false}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            <FiUserPlus /> Assign
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!list.length && (
                    <tr>
                      <td colSpan={showCsrAssignmentActions ? 6 : 5} className="px-3 py-12 text-center text-gray-400">
                        {tab === "assign" ? "No customers in the pool" : "No active assignments"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

        {chatCustomer && (
          <SalesCustomerChat customer={chatCustomer} onClose={() => setChatCustomer(null)} />
        )}

        {callModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
              <h3 className="font-bold text-gray-900 mb-3">Log call</h3>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Duration (seconds)</label>
              <input
                type="number"
                min={0}
                value={callModal.duration_sec}
                onChange={(e) => setCallModal((m) => ({ ...m, duration_sec: e.target.value }))}
                className={`${inputCls} w-full mb-3`}
              />
              <textarea
                placeholder="Notes (optional)"
                value={callModal.notes}
                onChange={(e) => setCallModal((m) => ({ ...m, notes: e.target.value }))}
                className={`${inputCls} w-full mb-4 min-h-[60px]`}
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setCallModal(null)} className="px-4 py-2 text-sm rounded-lg border">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleLogCall}
                  className="px-4 py-2 text-sm rounded-lg text-white"
                  style={{ backgroundColor: THEME.primary }}
                >
                  Save call log
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers style={{ color: THEME.primary }} /> Sales CRM
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Self-assign up to {stats?.maxCustomers || 100} customers · chat · call · track pipeline
            </p>
          </div>
          {stats && (
            <div className="text-right text-sm">
              <p className="font-semibold text-gray-800">
                Active: {stats.activeCustomers}/{stats.maxCustomers}
              </p>
              {stats.today && (
                <p className="text-gray-500 text-xs mt-1">
                  Today: {stats.today.calls_count || 0} calls ·{" "}
                  {Math.round((stats.today.calls_duration_sec || 0) / 60)} min
                </p>
              )}
            </div>
          )}
        </div>

        {isSalesAgent && (
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "mine", label: "My Customers" },
              { id: "pool", label: "Available Pool" },
              ...(isSalesLead ? [{ id: "team", label: "Team View" }] : []),
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  tab === t.id ? "text-white shadow" : "bg-gray-100 text-gray-600"
                }`}
                style={tab === t.id ? { backgroundColor: THEME.primary } : {}}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === "pool" && quota && (
          <div
            className={`rounded-lg px-4 py-2 text-sm mb-4 ${
              quota.canAssignMore ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
            }`}
          >
            <strong>Your batch:</strong> {quota.activeUnresolved}/{quota.maxBatch} active ·{" "}
            <strong>{quota.remainingSlots}</strong> slot(s) left
            {!quota.canAssignMore && (
              <span className="block mt-1">
                Resolve customers (Converted/Lost) to assign the next {MAX_BATCH}.
              </span>
            )}
          </div>
        )}

        {tab === "pool" && quota?.canAssignMore !== false && (
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <button
              type="button"
              onClick={selectAllVisible}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100"
            >
              Select all (up to {remainingSlots})
            </button>
            <button type="button" onClick={clearSelection} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100">
              Clear
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleBulkAssign}
                disabled={assigning}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                Assign selected ({selectedIds.size}) to me
              </button>
            )}
          </div>
        )}

        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${inputCls} w-full max-w-md mb-4`}
        />

        {loading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {tab === "pool" && (
                    <th className="px-3 py-2 w-10">
                      <span className="sr-only">Select</span>
                    </th>
                  )}
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500">Premium</th>
                  {tab !== "pool" && (
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Stage</th>
                  )}
                  {(tab === "team" || showCsrAssignmentActions) && (
                    <th className="px-3 py-2 text-left font-semibold text-gray-500">Agent</th>
                  )}
                  <th className="px-3 py-2 text-right font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.userId || c.assignmentId} className="border-t border-gray-100">
                    {tab === "pool" && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.userId)}
                          disabled={!canSelectMore(c.userId)}
                          onChange={() => toggleSelect(c.userId)}
                          className="rounded border-gray-300"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900">
                        {c.firstName} {c.lastName}
                      </p>
                      <p className="text-xs text-gray-400">{c.email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          c.isPremium ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.isPremium ? "Premium" : "Free"}
                      </span>
                    </td>
                    {tab !== "pool" && (
                      <td className="px-3 py-2">
                        <select
                          value={stages.find((s) => s.stage_key === c.stageKey)?.id || ""}
                          onChange={(e) => handleStageChange(c.assignmentId, Number(e.target.value))}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                        >
                          {stages.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    {tab === "team" && (
                      <td className="px-3 py-2 text-gray-600 text-xs">{c.agentName || "—"}</td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {showPoolActions && (
                          <button
                            type="button"
                            onClick={() => handleAssign(c.userId)}
                            disabled={assigning || quota?.canAssignMore === false}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            <FiUserPlus /> Assign
                          </button>
                        )}
                        {showAssignedActions && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleContact(c)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700"
                            >
                              <FiMessageCircle /> Contact
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setCallModal({ userId: c.userId, duration_sec: 5, notes: "" })
                              }
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700"
                            >
                              <FiPhone /> Log call
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setBlockModal({
                                  userId: c.userId,
                                  name: `${c.firstName} ${c.lastName}`,
                                })
                              }
                              className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-700"
                            >
                              Block
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length && (
                  <tr>
                    <td colSpan={6} className="px-3 py-12 text-center text-gray-400">
                      No customers in this view
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {chatCustomer && (
        <SalesCustomerChat customer={chatCustomer} onClose={() => setChatCustomer(null)} />
      )}

      {blockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-bold text-gray-900 mb-2">Block {blockModal.name}</h3>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Reason</label>
            <select
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              className={`${inputCls} w-full mb-3`}
            >
              {BLOCK_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <textarea
              placeholder="Additional details (optional)"
              value={blockDetail}
              onChange={(e) => setBlockDetail(e.target.value)}
              className={`${inputCls} w-full mb-4 min-h-[80px]`}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setBlockModal(null)} className="px-4 py-2 text-sm rounded-lg border">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBlock}
                className="px-4 py-2 text-sm rounded-lg text-white"
                style={{ backgroundColor: THEME.primary }}
              >
                Block customer
              </button>
            </div>
          </div>
        </div>
      )}

      {callModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-bold text-gray-900 mb-3">Log sales call</h3>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Duration (seconds)</label>
            <input
              type="number"
              min={0}
              value={callModal.duration_sec}
              onChange={(e) => setCallModal((m) => ({ ...m, duration_sec: e.target.value }))}
              className={`${inputCls} w-full mb-3`}
            />
            <textarea
              placeholder="Notes (optional)"
              value={callModal.notes}
              onChange={(e) => setCallModal((m) => ({ ...m, notes: e.target.value }))}
              className={`${inputCls} w-full mb-4 min-h-[60px]`}
            />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setCallModal(null)} className="px-4 py-2 text-sm rounded-lg border">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogCall}
                className="px-4 py-2 text-sm rounded-lg text-white"
                style={{ backgroundColor: THEME.primary }}
              >
                Save call log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
